import { createSlice } from "@reduxjs/toolkit";
import {
  BaseResponse,
  BaseResponseSingle,
  CategoryRelationshipResponse,
  CategoryResponse,
  SecondSubCategoryResponse,
  SubcategoryResponse,
} from "../../typings/interfaces";
import {
  getAlterationPrices,
  getAlterationSubCategories,
  getCategoryAction,
  getPricing,
  getFabricTypes,
  getSecondSubCategoryAction,
  getSubCategoryAction,
  getCategoryRelationship,
  getCategoryMaterialRelationship,
  getSubCategoryMaterialRelationship,
  uploadPriceList,
  getPriceListAction,
  reorderCategoriesAction,
  reorderSubCategoriesAction,
  createSubcategoryWithImageAction,
  createMaterialAction,
  reorderMaterialsAction,
  createSecondSubcategoryAction,
  reorderSecondSubcategoriesAction,
  updateCategoryAction,
  updateSubcategoryAction,
  updateMaterialAction,
  updateSecondSubcategoryAction,
  upsertPriceAction,
  deleteCategoryAction,
  deleteSubcategoryAction,
  deleteMaterialAction,
  deleteSecondSubcategoryAction,
} from "../actions/pricingAction";
import { PricingResponse } from "../../typings/interfaces/responses/pricing-response";
import { AlterationPriceResponse } from "../../typings/interfaces/responses/alteration-price-response";
import { AlterationSubCategoryResponse } from "../../typings/interfaces/responses/alteration-sub-category-response";
import { FabricTypeResponse } from "../../typings/interfaces/responses/fabricTypeResponse";
import { CategoryMaterialRelationshipResponse } from "../../typings/interfaces/responses/category-material-relationship";
import { SubCategoryMaterialRelationshipResponse } from "../../typings/interfaces/responses/sub-category-material-relationship";
import { IPriceResponseBody } from "../../typings/interfaces/responses/price-list-response";

export interface PriceState {
  categoryResponse: BaseResponse<CategoryResponse> | null;
  categoryResponseLoading: boolean;
  categoryResponseError: string | null;

  categoryRelationshipResponse: BaseResponse<CategoryRelationshipResponse> | null;
  categoryRelationshipResponseLoading: boolean;
  categoryRelationshipResponseError: string | null;

  subCategoryResponse: BaseResponse<SubcategoryResponse> | null;
  subCategoryResponseLoading: boolean;
  subCategoryResponseError: string | null;

  secondSubCategoryResponse: BaseResponse<SecondSubCategoryResponse> | null;
  secondSubCategoryResponseLoading: boolean;
  secondSubCategoryResponseError: string | null;

  dryCleanResponse: BaseResponse<PricingResponse> | null;
  dryCleanResponseLoading: boolean;
  dryCleanResponseError: string | null;

  alterationModificationType: Array<PricingResponse> | null;

  alterationPrices: Array<PricingResponse> | null;

  alterationPriceResponse: BaseResponse<AlterationPriceResponse> | null;
  alterationPriceResponseError: string | null;
  alterationPriceResponseLoading: boolean;

  alterationSubCategoryResponse: BaseResponse<AlterationSubCategoryResponse> | null;
  alterationSubCategoryResponseError: string | null;
  alterationSubCategoryResponseLoading: boolean;

  fabricTypesResponse: BaseResponse<FabricTypeResponse> | null;
  fabricTypesResponseError: string | null;
  fabricTypesResponseLoading: boolean;

  categoryMaterialRelationship: BaseResponse<CategoryMaterialRelationshipResponse> | null;
  categoryMaterialRelationshipLoading: boolean;
  categoryMaterialRelationshipError: string | null;

  subCategoryMaterialRelationship: BaseResponse<SubCategoryMaterialRelationshipResponse> | null;
  subCategoryMaterialRelationshipLoading: boolean;
  subCategoryMaterialRelationshipError: string | null;

  uploadPriceListResponse: BaseResponse<any> | null;
  uploadPriceListResponseLoading: boolean;
  uploadPriceListResponseError: string | null;

  priceListData: IPriceResponseBody | null;
  priceListLoading: boolean;
  priceListError: string | null;

  reorderCategoriesLoading: boolean;
  reorderCategoriesError: string | null;

  reorderSubCategoriesLoading: boolean;
  reorderSubCategoriesError: string | null;

  createSubcategoryLoading: boolean;
  createSubcategoryError: string | null;

  createMaterialLoading: boolean;
  createMaterialError: string | null;

  reorderMaterialsLoading: boolean;
  reorderMaterialsError: string | null;

  createSecondSubcategoryLoading: boolean;
  createSecondSubcategoryError: string | null;

  reorderSecondSubcategoriesLoading: boolean;
  reorderSecondSubcategoriesError: string | null;

  upsertPriceLoading: boolean;
  upsertPriceError: string | null;

  deleteCategoryLoading: boolean;
  deleteSubcategoryLoading: boolean;
  deleteMaterialLoading: boolean;
  deleteSecondSubcategoryLoading: boolean;

  updateCategoryLoading: boolean;
  updateCategoryError: string | null;

  updateSubcategoryLoading: boolean;
  updateSubcategoryError: string | null;

  updateMaterialLoading: boolean;
  updateMaterialError: string | null;

  updateSecondSubcategoryLoading: boolean;
  updateSecondSubcategoryError: string | null;
}

const initialState: PriceState = {
  categoryResponse: null,
  categoryResponseError: null,
  categoryResponseLoading: false,

  secondSubCategoryResponse: null,
  secondSubCategoryResponseError: null,
  secondSubCategoryResponseLoading: false,

  subCategoryResponse: null,
  subCategoryResponseError: null,
  subCategoryResponseLoading: false,

  dryCleanResponse: null,
  dryCleanResponseError: null,
  dryCleanResponseLoading: false,

  alterationPriceResponse: null,
  alterationPriceResponseError: null,
  alterationPriceResponseLoading: false,

  alterationSubCategoryResponse: null,
  alterationSubCategoryResponseError: null,
  alterationSubCategoryResponseLoading: false,

  fabricTypesResponse: null,
  fabricTypesResponseError: null,
  fabricTypesResponseLoading: false,

  categoryRelationshipResponse: null,
  categoryRelationshipResponseLoading: false,
  categoryRelationshipResponseError: null,

  categoryMaterialRelationship: null,
  categoryMaterialRelationshipLoading: false,
  categoryMaterialRelationshipError: null,

  subCategoryMaterialRelationship: null,
  subCategoryMaterialRelationshipLoading: false,
  subCategoryMaterialRelationshipError: null,

  uploadPriceListResponse: null,
  uploadPriceListResponseLoading: false,
  uploadPriceListResponseError: null,

  alterationModificationType: [],
  alterationPrices: [],

  priceListData: null,
  priceListLoading: false,
  priceListError: null,

  reorderCategoriesLoading: false,
  reorderCategoriesError: null,

  reorderSubCategoriesLoading: false,
  reorderSubCategoriesError: null,

  createSubcategoryLoading: false,
  createSubcategoryError: null,

  createMaterialLoading: false,
  createMaterialError: null,

  reorderMaterialsLoading: false,
  reorderMaterialsError: null,

  createSecondSubcategoryLoading: false,
  createSecondSubcategoryError: null,

  reorderSecondSubcategoriesLoading: false,
  reorderSecondSubcategoriesError: null,

  upsertPriceLoading: false,
  upsertPriceError: null,

  deleteCategoryLoading: false,
  deleteSubcategoryLoading: false,
  deleteMaterialLoading: false,
  deleteSecondSubcategoryLoading: false,

  updateCategoryLoading: false,
  updateCategoryError: null,

  updateSubcategoryLoading: false,
  updateSubcategoryError: null,

  updateMaterialLoading: false,
  updateMaterialError: null,

  updateSecondSubcategoryLoading: false,
  updateSecondSubcategoryError: null,
};

const pricingSlice = createSlice({
  name: "pricing",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getCategoryAction.fulfilled, (state, action) => {
      state.categoryResponse = action.payload.response;
      state.categoryResponseLoading = false;
      state.categoryResponseError = null;
    });
    builder.addCase(getCategoryAction.pending, (state, action) => {
      state.categoryResponse = null;
      state.categoryResponseLoading = true;
      state.categoryResponseError = null;
    });
    builder.addCase(getCategoryAction.rejected, (state, action) => {
      state.categoryResponse = null;
      state.categoryResponseLoading = false;
      state.categoryResponseError = action.error as string;
    });

    builder.addCase(getSubCategoryAction.fulfilled, (state, action) => {
      state.subCategoryResponse = action.payload.response;
      state.subCategoryResponseLoading = false;
      state.subCategoryResponseError = null;
    });
    builder.addCase(getSubCategoryAction.pending, (state, action) => {
      state.subCategoryResponse = null;
      state.subCategoryResponseLoading = true;
      state.subCategoryResponseError = null;
    });
    builder.addCase(getSubCategoryAction.rejected, (state, action) => {
      state.subCategoryResponse = null;
      state.subCategoryResponseLoading = false;
      state.subCategoryResponseError = action.error as string;
    });

    builder.addCase(getSecondSubCategoryAction.fulfilled, (state, action) => {
      state.secondSubCategoryResponse = action.payload.response;
      state.secondSubCategoryResponseLoading = false;
      state.secondSubCategoryResponseError = null;
    });
    builder.addCase(getSecondSubCategoryAction.pending, (state, action) => {
      state.secondSubCategoryResponse = null;
      state.secondSubCategoryResponseLoading = true;
      state.secondSubCategoryResponseError = null;
    });
    builder.addCase(getSecondSubCategoryAction.rejected, (state, action) => {
      state.secondSubCategoryResponse = null;
      state.secondSubCategoryResponseLoading = false;
      state.secondSubCategoryResponseError = action.error as string;
    });

    builder.addCase(getPricing.fulfilled, (state, action) => {
      state.dryCleanResponse = action.payload.response;
      const filtered = action.payload.response?.body?.filter(
        (modification) =>
          modification.categoryName.includes("Alteration") ||
          modification.categoryName.includes("alteration")
      );
      const distinct = (value: any, index: any, self: any) => {
        return (
          self.findIndex((e: any) => e.materialName === value.materialName) ===
          index
        );
      };

      const uniqueList = filtered?.filter(distinct);
      state.alterationModificationType = uniqueList;
      state.alterationPrices = filtered;
      state.dryCleanResponseLoading = false;
      state.dryCleanResponseError = null;
    });
    builder.addCase(getPricing.pending, (state, action) => {
      state.dryCleanResponse = null;
      state.dryCleanResponseLoading = true;
      state.dryCleanResponseError = null;
      state.alterationModificationType = [];
    });
    builder.addCase(getPricing.rejected, (state, action) => {
      state.dryCleanResponse = null;
      state.dryCleanResponseLoading = false;
      state.dryCleanResponseError = action.error as string;
      state.alterationModificationType = [];
    });

    builder.addCase(getAlterationPrices.fulfilled, (state, action) => {
      state.alterationPriceResponse = action.payload.response;
      state.alterationPriceResponseLoading = false;
      state.alterationPriceResponseError = null;
    });
    builder.addCase(getAlterationPrices.pending, (state, action) => {
      state.alterationPriceResponse = null;
      state.alterationPriceResponseLoading = true;
      state.alterationPriceResponseError = null;
    });
    builder.addCase(getAlterationPrices.rejected, (state, action) => {
      state.alterationPriceResponse = null;
      state.alterationPriceResponseLoading = false;
      state.alterationPriceResponseError = action.error as string;
    });

    builder.addCase(getAlterationSubCategories.fulfilled, (state, action) => {
      state.alterationSubCategoryResponse = action.payload.response;
      state.alterationSubCategoryResponseLoading = false;
      state.alterationSubCategoryResponseError = null;
    });
    builder.addCase(getAlterationSubCategories.pending, (state, action) => {
      state.alterationSubCategoryResponse = null;
      state.alterationSubCategoryResponseLoading = true;
      state.alterationSubCategoryResponseError = null;
    });
    builder.addCase(getAlterationSubCategories.rejected, (state, action) => {
      state.alterationSubCategoryResponse = null;
      state.alterationSubCategoryResponseLoading = false;
      state.alterationSubCategoryResponseError = action.error as string;
    });

    builder.addCase(getFabricTypes.fulfilled, (state, action) => {
      state.fabricTypesResponse = action.payload.response;
      state.fabricTypesResponseLoading = false;
      state.fabricTypesResponseError = null;
    });
    builder.addCase(getFabricTypes.pending, (state, action) => {
      state.fabricTypesResponse = null;
      state.fabricTypesResponseLoading = true;
      state.fabricTypesResponseError = null;
    });
    builder.addCase(getFabricTypes.rejected, (state, action) => {
      state.fabricTypesResponse = null;
      state.fabricTypesResponseLoading = false;
      state.fabricTypesResponseError = action.error as string;
    });

    builder.addCase(getCategoryRelationship.fulfilled, (state, action) => {
      state.categoryRelationshipResponse = action.payload.response;
      state.categoryRelationshipResponseLoading = false;
      state.categoryRelationshipResponseError = null;
    });
    builder.addCase(getCategoryRelationship.pending, (state, action) => {
      state.categoryRelationshipResponse = null;
      state.categoryRelationshipResponseLoading = true;
      state.categoryRelationshipResponseError = null;
    });
    builder.addCase(getCategoryRelationship.rejected, (state, action) => {
      state.categoryRelationshipResponse = null;
      state.categoryRelationshipResponseLoading = false;
      state.categoryRelationshipResponseError = action.error as string;
    });

    builder.addCase(
      getCategoryMaterialRelationship.fulfilled,
      (state, action) => {
        state.categoryMaterialRelationship = action.payload.response;
        state.categoryMaterialRelationshipLoading = false;
        state.categoryMaterialRelationshipError = null;
      }
    );
    builder.addCase(
      getCategoryMaterialRelationship.pending,
      (state, action) => {
        state.categoryMaterialRelationship = null;
        state.categoryMaterialRelationshipLoading = true;
        state.categoryMaterialRelationshipError = null;
      }
    );
    builder.addCase(
      getCategoryMaterialRelationship.rejected,
      (state, action) => {
        state.categoryMaterialRelationship = null;
        state.categoryMaterialRelationshipLoading = false;
        state.categoryMaterialRelationshipError = action.error as string;
      }
    );

    builder.addCase(
      getSubCategoryMaterialRelationship.fulfilled,
      (state, action) => {
        state.subCategoryMaterialRelationship = action.payload.response;
        state.subCategoryMaterialRelationshipLoading = false;
        state.subCategoryMaterialRelationshipError = null;
      }
    );
    builder.addCase(
      getSubCategoryMaterialRelationship.pending,
      (state, action) => {
        state.subCategoryMaterialRelationship = null;
        state.subCategoryMaterialRelationshipLoading = true;
        state.subCategoryMaterialRelationshipError = null;
      }
    );
    builder.addCase(
      getSubCategoryMaterialRelationship.rejected,
      (state, action) => {
        state.subCategoryMaterialRelationship = null;
        state.subCategoryMaterialRelationshipLoading = false;
        state.subCategoryMaterialRelationshipError = action.error as string;
      }
    );

    builder.addCase(uploadPriceList.fulfilled, (state, action) => {
      state.uploadPriceListResponse = action.payload.response;
      state.uploadPriceListResponseLoading = false;
      state.uploadPriceListResponseError = null;
    });
    builder.addCase(uploadPriceList.pending, (state, action) => {
      state.uploadPriceListResponse = null;
      state.uploadPriceListResponseLoading = true;
      state.uploadPriceListResponseError = null;
    });
    builder.addCase(uploadPriceList.rejected, (state, action) => {
      state.uploadPriceListResponse = null;
      state.uploadPriceListResponseLoading = false;
      state.uploadPriceListResponseError = action.error as string;
    });
    builder.addCase(getPriceListAction.pending, (state) => {
      state.priceListData = null;
      state.priceListLoading = true;
      state.priceListError = null;
    });
    builder.addCase(getPriceListAction.fulfilled, (state, action) => {
      state.priceListData = action.payload.response.body;
      state.priceListLoading = false;
      state.priceListError = null;
    });
    builder.addCase(getPriceListAction.rejected, (state, action) => {
      state.priceListData = null;
      state.priceListLoading = false;
      state.priceListError =
        typeof action.payload === "string"
          ? action.payload
          : (action.error.message as string);
    });

    builder.addCase(reorderCategoriesAction.pending, (state) => {
      state.reorderCategoriesLoading = true;
      state.reorderCategoriesError = null;
    });
    builder.addCase(reorderCategoriesAction.fulfilled, (state) => {
      state.reorderCategoriesLoading = false;
      state.reorderCategoriesError = null;
    });
    builder.addCase(reorderCategoriesAction.rejected, (state, action) => {
      state.reorderCategoriesLoading = false;
      state.reorderCategoriesError = action.error.message as string;
    });

    builder.addCase(reorderSubCategoriesAction.pending, (state) => {
      state.reorderSubCategoriesLoading = true;
      state.reorderSubCategoriesError = null;
    });
    builder.addCase(reorderSubCategoriesAction.fulfilled, (state) => {
      state.reorderSubCategoriesLoading = false;
      state.reorderSubCategoriesError = null;
    });
    builder.addCase(reorderSubCategoriesAction.rejected, (state, action) => {
      state.reorderSubCategoriesLoading = false;
      state.reorderSubCategoriesError = action.error.message as string;
    });

    builder.addCase(createSubcategoryWithImageAction.pending, (state) => {
      state.createSubcategoryLoading = true;
      state.createSubcategoryError = null;
    });
    builder.addCase(createSubcategoryWithImageAction.fulfilled, (state) => {
      state.createSubcategoryLoading = false;
      state.createSubcategoryError = null;
    });
    builder.addCase(createSubcategoryWithImageAction.rejected, (state, action) => {
      state.createSubcategoryLoading = false;
      state.createSubcategoryError = action.error.message as string;
    });

    builder.addCase(createMaterialAction.pending, (state) => {
      state.createMaterialLoading = true;
      state.createMaterialError = null;
    });
    builder.addCase(createMaterialAction.fulfilled, (state) => {
      state.createMaterialLoading = false;
      state.createMaterialError = null;
    });
    builder.addCase(createMaterialAction.rejected, (state, action) => {
      state.createMaterialLoading = false;
      state.createMaterialError = action.error.message as string;
    });

    builder.addCase(reorderMaterialsAction.pending, (state) => {
      state.reorderMaterialsLoading = true;
      state.reorderMaterialsError = null;
    });
    builder.addCase(reorderMaterialsAction.fulfilled, (state) => {
      state.reorderMaterialsLoading = false;
      state.reorderMaterialsError = null;
    });
    builder.addCase(reorderMaterialsAction.rejected, (state, action) => {
      state.reorderMaterialsLoading = false;
      state.reorderMaterialsError = action.error.message as string;
    });

    builder.addCase(createSecondSubcategoryAction.pending, (state) => {
      state.createSecondSubcategoryLoading = true;
      state.createSecondSubcategoryError = null;
    });
    builder.addCase(createSecondSubcategoryAction.fulfilled, (state) => {
      state.createSecondSubcategoryLoading = false;
      state.createSecondSubcategoryError = null;
    });
    builder.addCase(createSecondSubcategoryAction.rejected, (state, action) => {
      state.createSecondSubcategoryLoading = false;
      state.createSecondSubcategoryError = action.error.message as string;
    });

    builder.addCase(reorderSecondSubcategoriesAction.pending, (state) => {
      state.reorderSecondSubcategoriesLoading = true;
      state.reorderSecondSubcategoriesError = null;
    });
    builder.addCase(reorderSecondSubcategoriesAction.fulfilled, (state) => {
      state.reorderSecondSubcategoriesLoading = false;
      state.reorderSecondSubcategoriesError = null;
    });
    builder.addCase(reorderSecondSubcategoriesAction.rejected, (state, action) => {
      state.reorderSecondSubcategoriesLoading = false;
      state.reorderSecondSubcategoriesError = action.error.message as string;
    });

    builder.addCase(deleteCategoryAction.pending, (state) => { state.deleteCategoryLoading = true; });
    builder.addCase(deleteCategoryAction.fulfilled, (state) => { state.deleteCategoryLoading = false; });
    builder.addCase(deleteCategoryAction.rejected, (state) => { state.deleteCategoryLoading = false; });

    builder.addCase(deleteSubcategoryAction.pending, (state) => { state.deleteSubcategoryLoading = true; });
    builder.addCase(deleteSubcategoryAction.fulfilled, (state) => { state.deleteSubcategoryLoading = false; });
    builder.addCase(deleteSubcategoryAction.rejected, (state) => { state.deleteSubcategoryLoading = false; });

    builder.addCase(deleteMaterialAction.pending, (state) => { state.deleteMaterialLoading = true; });
    builder.addCase(deleteMaterialAction.fulfilled, (state) => { state.deleteMaterialLoading = false; });
    builder.addCase(deleteMaterialAction.rejected, (state) => { state.deleteMaterialLoading = false; });

    builder.addCase(deleteSecondSubcategoryAction.pending, (state) => { state.deleteSecondSubcategoryLoading = true; });
    builder.addCase(deleteSecondSubcategoryAction.fulfilled, (state) => { state.deleteSecondSubcategoryLoading = false; });
    builder.addCase(deleteSecondSubcategoryAction.rejected, (state) => { state.deleteSecondSubcategoryLoading = false; });

    builder.addCase(upsertPriceAction.pending, (state) => {
      state.upsertPriceLoading = true;
      state.upsertPriceError = null;
    });
    builder.addCase(upsertPriceAction.fulfilled, (state) => {
      state.upsertPriceLoading = false;
      state.upsertPriceError = null;
    });
    builder.addCase(upsertPriceAction.rejected, (state, action) => {
      state.upsertPriceLoading = false;
      state.upsertPriceError = action.error.message as string;
    });

    builder.addCase(updateCategoryAction.pending, (state) => {
      state.updateCategoryLoading = true;
      state.updateCategoryError = null;
    });
    builder.addCase(updateCategoryAction.fulfilled, (state) => {
      state.updateCategoryLoading = false;
      state.updateCategoryError = null;
    });
    builder.addCase(updateCategoryAction.rejected, (state, action) => {
      state.updateCategoryLoading = false;
      state.updateCategoryError = action.error.message as string;
    });

    builder.addCase(updateSubcategoryAction.pending, (state) => {
      state.updateSubcategoryLoading = true;
      state.updateSubcategoryError = null;
    });
    builder.addCase(updateSubcategoryAction.fulfilled, (state) => {
      state.updateSubcategoryLoading = false;
      state.updateSubcategoryError = null;
    });
    builder.addCase(updateSubcategoryAction.rejected, (state, action) => {
      state.updateSubcategoryLoading = false;
      state.updateSubcategoryError = action.error.message as string;
    });

    builder.addCase(updateMaterialAction.pending, (state) => {
      state.updateMaterialLoading = true;
      state.updateMaterialError = null;
    });
    builder.addCase(updateMaterialAction.fulfilled, (state) => {
      state.updateMaterialLoading = false;
      state.updateMaterialError = null;
    });
    builder.addCase(updateMaterialAction.rejected, (state, action) => {
      state.updateMaterialLoading = false;
      state.updateMaterialError = action.error.message as string;
    });

    builder.addCase(updateSecondSubcategoryAction.pending, (state) => {
      state.updateSecondSubcategoryLoading = true;
      state.updateSecondSubcategoryError = null;
    });
    builder.addCase(updateSecondSubcategoryAction.fulfilled, (state) => {
      state.updateSecondSubcategoryLoading = false;
      state.updateSecondSubcategoryError = null;
    });
    builder.addCase(updateSecondSubcategoryAction.rejected, (state, action) => {
      state.updateSecondSubcategoryLoading = false;
      state.updateSecondSubcategoryError = action.error.message as string;
    });
  },
});

export default pricingSlice.reducer;
export const PricingSliceAction = pricingSlice.actions;
